import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { LocationProvider, ErrorBoundary, Router, Route, useLocation } from 'preact-iso';
import { AppState, loadAppState } from './lib/app-state.js';
import QuotesPage from './pages/QuotesPage.jsx';
import QuoteEditor from './pages/QuoteEditor.jsx';
import SendPage from './pages/SendPage.jsx';
import EmailPage from './pages/EmailPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import './styles.css';

/* App Bridge raises shopify:navigate when an s-app-nav / s-link is chosen; route it inside the app. */
function NavBridge() {
  const { route } = useLocation();
  useEffect(() => {
    const go = (e) => {
      const href = e.target?.getAttribute?.('href');
      if (href && href.startsWith('/')) route(href);
    };
    document.addEventListener('shopify:navigate', go);
    return () => document.removeEventListener('shopify:navigate', go);
  }, [route]);
  return null;
}

function NotFound() {
  return (
    <s-page heading="Not found">
      <s-section>
        <s-paragraph>That page does not exist.</s-paragraph>
        <s-link href="/">Back to quotes</s-link>
      </s-section>
    </s-page>
  );
}

function App() {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const reload = () => loadAppState().then(setState).catch((e) => setError(e.message || String(e)));
  useEffect(() => { reload(); }, []);

  if (error) {
    return (
      <s-page heading="AFX Quotes">
        <s-banner tone="critical" heading="The app could not load">
          <s-paragraph>{error}</s-paragraph>
          <s-button slot="secondary-actions" onClick={() => { setError(''); reload(); }}>Try again</s-button>
        </s-banner>
      </s-page>
    );
  }
  if (!state) {
    return (
      <s-page heading="AFX Quotes">
        <s-section>
          <s-stack alignItems="center" padding="large">
            <s-spinner accessibilityLabel="Loading" size="large" />
          </s-stack>
        </s-section>
      </s-page>
    );
  }
  return (
    <AppState.Provider value={{ ...state, refresh: reload, setSettings: (settings) => setState((s) => ({ ...s, settings })) }}>
      <LocationProvider>
        <NavBridge />
        <s-app-nav>
          <s-link href="/" rel="home">Quotes</s-link>
          <s-link href="/quotes/new">New quote</s-link>
          <s-link href="/email">Email</s-link>
          <s-link href="/settings">Settings</s-link>
        </s-app-nav>
        <ErrorBoundary>
          <Router>
            <Route path="/" component={QuotesPage} />
            <Route path="/quotes/new" component={QuoteEditor} />
            <Route path="/quotes/:id" component={QuoteEditor} />
            <Route path="/quotes/:id/send" component={SendPage} />
            <Route path="/email" component={EmailPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route default component={NotFound} />
          </Router>
        </ErrorBoundary>
      </LocationProvider>
    </AppState.Provider>
  );
}

const root = document.createElement('div');
document.body.appendChild(root);
render(<App />, root);
